<template>
    <BaseDialog
        v-model="dialog"
        width="600"
        :title="
            $t(
                'message.familyAccounts.' +
                    (action === 'add' ? 'addAccountTitle' : 'editAccountTitle'),
                {
                    fullName: formData.fullName,
                }
            )
        "
        titleClass="tw-text-xl"
        :subtitle="
            $t(
                'message.familyAccounts.' +
                    (action === 'add'
                        ? 'addAccountDescription'
                        : 'editAccountDescription')
            )
        "
        :showCloseButton="true"
        :onClose="onClose"
    >
        <ValidationObserver ref="observer">
            <v-form ref="form">
                <v-row>
                    <v-col cols="6">
                        <ValidationProvider
                            v-slot="{ errors }"
                            :name="$t('message.fullName')"
                            rules="required|max:64"
                        >
                            <v-label class="tw-text-sm">
                                <span class="tw-font-semibold">{{
                                    $t('message.fullName')
                                }}</span>
                                <span class="tw-text-red-500 tw-ml-1">*</span>
                            </v-label>
                            <v-text-field
                                outlined
                                v-model="formData.fullName"
                                :error-messages="errors"
                            />
                        </ValidationProvider>
                    </v-col>
                    <v-col cols="6">
                        <ValidationProvider
                            v-slot="{ errors }"
                            :name="$t('message.familyAccounts.relationship')"
                            rules="required"
                        >
                            <v-label class="tw-text-sm">
                                <span class="tw-font-semibold">{{
                                    $t('message.familyAccounts.relationship')
                                }}</span>
                                <span class="tw-text-red-500 tw-ml-1">*</span>
                            </v-label>
                            <v-select
                                outlined
                                v-model="formData.relationship"
                                :items="Object.values(FamilyUserRelationship)"
                                :error-messages="errors"
                            >
                                <template #item="{ item }">
                                    <span class="tw-capitalize">{{
                                        item
                                    }}</span>
                                </template>
                                <template #selection="{ item }">
                                    <span class="tw-capitalize">{{
                                        item
                                    }}</span>
                                </template>
                            </v-select>
                        </ValidationProvider>
                    </v-col>
                    <v-col cols="6">
                        <v-label class="tw-text-sm"
                            ><span class="tw-font-semibold">{{
                                $t('message.dob')
                            }}</span>
                            <span class="tw-text-xs tw-italic">
                                ({{ $t('message.optional') }})</span
                            ></v-label
                        >
                        <DatePicker
                            v-model="formData.dob"
                            :max="new Date().toISOString()"
                            :outlined="true"
                            label=""
                            :placeholder="$t('message.pickADate')"
                            transition="none"
                        />
                    </v-col>
                    <v-col cols="6">
                        <ValidationProvider
                            v-slot="{ errors }"
                            :name="$t('message.email')"
                            rules="email|max:64"
                        >
                            <v-label class="tw-text-sm"
                                ><span class="tw-font-semibold">{{
                                    $t('message.email')
                                }}</span>
                                <span class="tw-text-xs tw-italic">
                                    ({{ $t('message.optional') }})</span
                                ></v-label
                            >
                            <v-text-field
                                outlined
                                v-model="formData.email"
                                :error-messages="errors"
                            />
                        </ValidationProvider>
                    </v-col>
                </v-row>
                <v-row v-if="showSendInvitationLink">
                    <v-col cols="12">
                        <v-alert type="error" text>
                            <span>
                                {{
                                    $t(
                                        'message.familyAccounts.emailAlreadyUsed',
                                        {
                                            email: formData.email,
                                        }
                                    )
                                }}</span
                            >
                            <span
                                @click="sendInvitation"
                                class="tw-uppercase tw-font-bold tw-underline tw-cursor-pointer"
                            >
                                {{
                                    $t(
                                        'message.familyAccounts.sendInvitationLinkButton'
                                    )
                                }}
                            </span>
                        </v-alert>
                    </v-col>
                </v-row>
            </v-form>
        </ValidationObserver>

        <v-alert v-if="errorMessage" type="error" text>
            <span>
                {{ errorMessage }}
            </span>
        </v-alert>

        <template #actions>
            <v-btn
                depressed
                color="transparent"
                class="text-uppercase"
                @click="onCancel"
                :disabled="isLoadingSubmit"
            >
                {{ $t('message.cancel') }}
            </v-btn>
            <v-btn
                depressed
                color="primary"
                dark
                class="text-uppercase"
                @click="onSubmit"
                :loading="isLoadingSubmit"
            >
                {{
                    action === 'add' ? $t('message.create') : $t('message.save')
                }}
            </v-btn>
        </template>
    </BaseDialog>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import DatePicker from '@/components/shared/DatePicker.vue'
import { FamilyUserRelationship } from '../types/subAccountRelationships'
import {
    createSubAccount,
    editSubAccount,
    sendSubAccountInvitation,
    EMAIL_IS_ALREADY_USED_ERROR,
} from '../api/subAccountsApi'
import { isVibefamGeneratedEmail } from '../utils/subAccountUtils'
import { useI18n } from 'vue-i18n-composable'
import { useToast } from '@/components/base/toast/composable/useToast'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import BaseDialog from '../../../components/base/dialog/BaseDialog.vue'
import { isEqual } from 'lodash'

const { t } = useI18n()
const { showSuccessToast, showErrorToast } = useToast()
const observer = ref(null)

const { action, mainAccountId, user, isOpen, onClose, refresh } = defineProps({
    action: String, // 'add' or 'edit'
    mainAccountId: String,
    user: { type: Object, default: {} },
    isOpen: Boolean,
    onClose: Function,
    refresh: Function,
})

const dialog = computed({
    get: () => isOpen,
    set: (val) => {
        if (!val) {
            onClose?.()
        }
    },
})
const isLoadingSubmit = ref(false)
const emptyFormData = {
    fullName: '',
    dob: '',
    email: '',
    relationship: '',
}
const formData = ref(emptyFormData)
const showSendInvitationLink = ref(false)
const initialFormData = ref({})
const errorMessage = ref('')

onMounted(() => {
    errorMessage.value = ''
    if (action === 'edit' && user) {
        const userFormData = {
            ...emptyFormData,
            fullName: user.fullName,
            relationship: user.relationship,
            dob: user.dob ?? '',
            email:
                user.email && !isVibefamGeneratedEmail(user.email)
                    ? user.email
                    : '',
        }
        formData.value = userFormData
        initialFormData.value = { ...userFormData }
    } else {
        initialFormData.value = { ...emptyFormData }
    }
})

const onCancel = () => {
    dialog.value = false
    formData.value = emptyFormData
}

const isFormChanged = computed(() => {
    return !isEqual(formData.value, initialFormData.value)
})

const getFormChanges = () => {
    return Object.keys(formData.value).reduce((acc, key) => {
        if (formData.value[key] !== initialFormData.value[key]) {
            acc[key] = formData.value[key]
        }
        return acc
    }, {})
}

const onSubmit = async () => {
    try {
        if (!isFormChanged.value) {
            errorMessage.value = t('message.noChanges')
            return
        }
        errorMessage.value = ''

        isLoadingSubmit.value = true
        const isValid = await observer.value.validate()
        if (!isValid) return

        let result
        if (action === 'add') {
            result = await createSubAccount({
                ...formData.value,
                mainAccountId,
            })
        } else {
            const formChanges = getFormChanges()
            result = await editSubAccount(user?.id, {
                ...formChanges,
                mainAccountId,
            })
        }

        if (result.success) {
            dialog.value = false
            formData.value = emptyFormData
            refresh?.()
            const context = { fullName: formData.value.fullName }
            const successMessage =
                action === 'add'
                    ? t(
                          'message.familyAccounts.addAccountSuccessMessage',
                          context
                      )
                    : t(
                          'message.familyAccounts.editAccountSuccessMessage',
                          context
                      )
            showSuccessToast(successMessage)
        } else {
            throw new Error(result.error)
        }
    } catch (error) {
        if (error === EMAIL_IS_ALREADY_USED_ERROR) {
            showSendInvitationLink.value = true
        } else {
            showErrorToast(error?.message)
        }
    } finally {
        isLoadingSubmit.value = false
    }
}

const sendInvitation = async () => {
    const inviteAccountData = {
        ...formData.value,
    }
    if (action === 'edit') {
        inviteAccountData.subAccountId = user?.id
    }
    const result = await sendSubAccountInvitation(inviteAccountData)
    if (result.success) {
        showSendInvitationLink.value = false
        onCancel()
        showSuccessToast(
            t('message.familyAccounts.sendInvitationLinkSuccessMessage')
        )
    } else {
        showErrorToast(result.error)
    }
}
</script>
